import React, { useState, useEffect } from 'react';
import Header from '../../sharedcomponents/header/header';
import { fetchApi } from '../../services/api';
import { buildCreateRolePayload, buildUpdateRolePayload, buildDeleteRolePayload, buildRoleGridEntry } from './payloads/RoleMasterPayload';
import { buildAuditLogPayload } from './payloads/AuditLogPayload';
import eyeIcon from '../../assets/images/eye.svg';
import editIcon from '../../assets/images/edit.svg';
import dustbinIcon from '../../assets/images/dustbin.svg';
import * as XLSX from 'xlsx';
import { sanitizeDataObject, containsFormulaInjection } from '../../utils/csvSanitizer';
import './css/masters.css';

const sessionList = [
  { name: 'Dashboard', isParent: true }, 
  { name: '8Ds', isParent: true },
  { name: 'Create New', isParent: true },
  { name: 'Reports', isParent: true },
  { name: 'Masters', isParent: true },
  { name: 'Role & Authorization Master', isParent: false },
  { name: 'User Master', isParent: false }   
];

const RoleMaster = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [editIndex, setEditIndex] = useState(null);
  const [newRole, setNewRole] = useState({ roleCode: '', roleDescription: '', status: true, sessionAccess: [] });
  const [apiMessage, setApiMessage] = useState({ text: '', type: '' });
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const pageSize = 7;

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await fetchApi('/api/RoleMaster/GetRoles', {
        method: 'GET',
      });

      
      // Handle different response structures
      let rolesData = [];
      if (Array.isArray(response)) {
        rolesData = response;
      } else if (response.data && Array.isArray(response.data)) {
        rolesData = response.data;
      } else if (response.result && Array.isArray(response.result)) {
        rolesData = response.result;
      } else if (response.roles && Array.isArray(response.roles)) {
        rolesData = response.roles;
      }
      
      // Map API response to grid format
      const mappedRoles = rolesData.map(role => {
        return {
        status: role.Status,
        roleCode: role.RoleCode,
        roleDescription: role.RoleDescription,
        sessionAccess: role.SessionAccess,
        insertedBy: role.CreatedBy || '',
        insertedOn: new Date(role.CreatedOn).toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }),
        modifiedBy: role.ModifiedBy || role.CreatedBy || '',
        modifiedOn: role.ModifiedOn ? new Date(role.ModifiedOn).toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }) : new Date(role.CreatedOn).toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }),
        originalCreatedBy: role.CreatedBy,
        originalModifiedBy: role.ModifiedBy,
        originalCreatedOn: role.CreatedOn,
        originalModifiedOn: role.ModifiedOn
      };
      });
      

      setRoles(mappedRoles);
    } catch (error) {
      console.error('[RoleMaster] GetRoles Error:', error);
      setApiMessage({ text: error.message || 'Failed to fetch roles', type: 'error' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
    } finally {
      setLoading(false);
    }
  };

  const filteredRoles = roles.filter((role) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      role.status?.toLowerCase().includes(term) ||
      role.roleCode?.toLowerCase().includes(term) ||
      role.roleDescription?.toLowerCase().includes(term) ||
      role.sessionAccess?.toLowerCase().includes(term) ||
      role.insertedBy?.toLowerCase().includes(term) ||
      role.modifiedBy?.toLowerCase().includes(term)
    );
  });

  const totalRecords = filteredRoles.length;
  const totalPages = Math.ceil(totalRecords / pageSize) || 1;
  const paginatedRoles = filteredRoles.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSort = (field) => {
    let direction = 'asc';
    if (sortField === field && sortDirection === 'asc') {
      direction = 'desc';
    }
    setSortField(field);
    setSortDirection(direction);
    const sorted = [...roles].sort((a, b) => {
      const valA = (a[field] || '').toLowerCase();
      const valB = (b[field] || '').toLowerCase();
      if (valA < valB) return direction === 'asc' ? -1 : 1;
      if (valA > valB) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    setRoles(sorted);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleView = (role) => {
    setModalMode('view');
    const accessList = role.sessionAccess ? role.sessionAccess.split(',').map(s => s.trim()) : [];
    setNewRole({
      roleCode: role.roleCode,
      roleDescription: role.roleDescription,
      status: role.status === 'Active',
      sessionAccess: accessList,
    });
    setShowModal(true);
  };

  const handleEdit = (role, index) => {
    setModalMode('edit');
    setEditIndex(index);
    const accessList = role.sessionAccess ? role.sessionAccess.split(',').map(s => s.trim()) : [];
    setNewRole({
      roleCode: role.roleCode,
      roleDescription: role.roleDescription,
      status: role.status === 'Active',
      sessionAccess: accessList,
    });
    setShowModal(true);
  };

  const handleDelete = async (role) => {
    if (window.confirm(`Are you sure you want to delete role "${role.roleDescription}"?`)) {
      try {
        const deletePayload = buildDeleteRolePayload(role);
        

        
        const response = await fetchApi('/api/RoleMaster/DeleteRole', {
          method: 'POST',
          body: JSON.stringify(deletePayload),
        });
        

        
        // Refresh grid from API to reflect changes
        await fetchRoles();
        
        setApiMessage({ text: 'Role deleted successfully', type: 'success' });
        setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      } catch (error) {
        console.error('[RoleMaster] DeleteRole Error:', error);
        setApiMessage({ text: error.message || 'Failed to delete role', type: 'error' });
        setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      }
    }
  };

  const handleStatusToggle = async (role, index) => {
    const newStatus = role.status === 'Active' ? 'Inactive' : 'Active';
    
    // If trying to inactivate a role, check if it's assigned to any users
    if (newStatus === 'Inactive') {
      try {

        const checkResponse = await fetchApi('/api/RoleMaster/CheckRoleAssignment', {
          method: 'POST',
          body: JSON.stringify({ RoleCode: role.roleCode }),
        });
        

        
        // Check if role is assigned to users
        const isAssigned = checkResponse?.data?.IsAssigned || checkResponse?.IsAssigned || false;
        const assignedCount = checkResponse?.data?.AssignedCount || checkResponse?.AssignedCount || 0;
        
        if (isAssigned && assignedCount > 0) {
          alert(`Cannot inactivate this role as it is currently assigned to ${assignedCount} user(s). Please unassign this role from all users first.`);
          return; // Exit without making any changes
        }
      } catch (error) {
        console.error('[RoleMaster] CheckRoleAssignment Error:', error);
        setApiMessage({ text: 'Failed to validate role assignment. Please try again.', type: 'error' });
        setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
        return; // Exit without making any changes
      }
    }

    // Optimistic update
    const updatedRoles = [...roles];
    updatedRoles[index] = { ...updatedRoles[index], status: newStatus };
    setRoles(updatedRoles);

    try {
      const updatedRole = {
        roleCode: role.roleCode,
        roleDescription: role.roleDescription,
        status: newStatus === 'Active',
        sessionAccess: Array.isArray(role.sessionAccess)
          ? role.sessionAccess
          : (role.sessionAccess ? role.sessionAccess.split(',').map(s => s.trim()) : []),
      };
      const payload = buildUpdateRolePayload(updatedRole, role);


      await fetchApi('/api/RoleMaster/UpdateRole', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      // Create Audit Log
      await fetchApi('/api/AuditLog/CreateAuditLog', {
        method: 'POST',
        body: JSON.stringify(buildAuditLogPayload(payload)),
        skipLoader: true
      }).catch(err => console.error('[RoleMaster] CreateAuditLog Error:', err));

      setApiMessage({ text: 'Role status updated successfully', type: 'success' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      // Revert on failure
      const revertedRoles = [...roles];
      revertedRoles[index] = { ...revertedRoles[index], status: role.status };
      setRoles(revertedRoles);
      console.error('[RoleMaster] StatusToggle Error:', error);
      setApiMessage({ text: error.message || 'Failed to update status', type: 'error' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
    }
  };

  const handleDownload = () => {
    try {
      // Prepare data for Excel export
      const excelData = roles.map((role, index) => sanitizeDataObject({
        'S.No': index + 1,
        'Status': role.status,
        'Role Code': role.roleCode,
        'Role Description': role.roleDescription,
        'Session Access': role.sessionAccess,
        'Inserted By': role.insertedBy,
        'Inserted On': role.insertedOn,
        'Modified By': role.modifiedBy,
        'Modified On': role.modifiedOn
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const columnWidths = [
        { wch: 8 },   // S.No
        { wch: 12 },  // Status
        { wch: 15 },  // Role Code
        { wch: 25 },  // Role Description
        { wch: 40 },  // Session Access
        { wch: 20 },  // Inserted By
        { wch: 18 },  // Inserted On
        { wch: 20 },  // Modified By
        { wch: 18 }   // Modified On
      ];
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Role Master');

      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `Role_Master_${currentDate}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);

      setApiMessage({ text: 'Excel file downloaded successfully', type: 'success' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      console.error('[RoleMaster] Excel Download Error:', error);
      setApiMessage({ text: 'Failed to download Excel file', type: 'error' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
    }
  };

  // Define parent-child relationships for session access
  const childrenOf = {
    'Masters': ['Role & Authorization Master', 'User Master']
  };

  const handleSessionToggle = (session) => {
    setNewRole((prev) => {
      const exists = prev.sessionAccess.includes(session);
      let updated;
      if (exists) {
        // Unchecking: remove this session and its children (if parent)
        const children = childrenOf[session] || [];
        updated = prev.sessionAccess.filter((s) => s !== session && !children.includes(s));
      } else {
        // Checking: add this session and its children (if parent)
        const children = childrenOf[session] || [];
        updated = [...new Set([...prev.sessionAccess, session, ...children])];
      }
      return { ...prev, sessionAccess: updated };
    });
  };

  const handleSelectAll = (checked) => {
    setNewRole((prev) => ({
      ...prev,
      sessionAccess: checked ? sessionList.map((s) => s.name) : [],
    }));
  };

  const handleHistoryDownload = () => {
    try {
      const excelData = historyData.map((item, index) => sanitizeDataObject({
        'S.No': index + 1,
        'Status': item.status,
        'Role Code': item.roleCode,
        'Role Description': item.roleDescription,
        'Session Access': item.sessionAccess,
        'Created By': item.createdBy,
        'Created On': item.createdOn,
        'Modified By': item.modifiedBy,
        'Modified On': item.modifiedOn
      }));
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Audit History');
      XLSX.writeFile(workbook, `Role_Audit_History_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('[RoleMaster] History Download Error:', error);
    }
  };

  const handleViewHistory = async (role) => {
    try {
      const response = await fetchApi('/api/AuditLog/GetAuditLogs', {
        method: 'POST',
        body: JSON.stringify({ RoleCode: role.roleCode }),
      });


      let historyRecords = [];
      if (Array.isArray(response)) {
        historyRecords = response;
      } else if (response.data && Array.isArray(response.data)) {
        historyRecords = response.data;
      } else if (response.result && Array.isArray(response.result)) {
        historyRecords = response.result;
      }

      // Sort by ModifiedOn date (most recent first)
      historyRecords.sort((a, b) => {
        const dateA = new Date(a.ModifiedOn || a.CreatedOn);
        const dateB = new Date(b.ModifiedOn || b.CreatedOn);
        return dateB - dateA;
      });

      const mappedHistory = historyRecords.map(item => ({
        status: item.Status,
        roleCode: item.RoleCode,
        roleDescription: item.RoleDescription,
        sessionAccess: item.SessionAccess,
        createdBy: item.CreatedBy || '',
        createdOn: item.CreatedOn ? new Date(item.CreatedOn).toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }) : '',
        modifiedBy: item.ModifiedBy || item.CreatedBy || '',
        modifiedOn: item.ModifiedOn ? new Date(item.ModifiedOn).toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }) : '',
      }));

      setHistoryData(mappedHistory);
      setShowHistoryModal(true);
    } catch (error) {
      console.error('[RoleMaster] GetAuditLogs Error:', error);
      setApiMessage({ text: error.message || 'Failed to fetch audit history', type: 'error' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
    }
  };

  const handleAddRole = async () => {
    const errors = [];
    if (!newRole.roleCode.trim()) errors.push('Role Code');
    if (!newRole.roleDescription.trim()) errors.push('Role Description');
    if (!newRole.sessionAccess || newRole.sessionAccess.length === 0) errors.push('Session Access');
    if (errors.length > 0) {
      setApiMessage({ text: `Please fill mandatory fields: ${errors.join(', ')}`, type: 'error' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      return;
    }
    // CSV Formula Injection validation
    if (containsFormulaInjection(newRole.roleCode) || containsFormulaInjection(newRole.roleDescription)) {
      setApiMessage({ text: `Invalid input: value cannot start with =, +, -, or @`, type: 'error' });
      setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      return;
    }
    if (modalMode === 'edit' && editIndex !== null) {
      try {
        const originalRole = roles[editIndex];
        const payload = buildUpdateRolePayload(newRole, originalRole);

        
        const response = await fetchApi('/api/RoleMaster/UpdateRole', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        

        
        // Create Audit Log
        await fetchApi('/api/AuditLog/CreateAuditLog', {
          method: 'POST',
          body: JSON.stringify(buildAuditLogPayload(payload)),
          skipLoader: true
        }).catch(err => console.error('[RoleMaster] CreateAuditLog Error:', err));

        // Update local state - refresh from API to maintain consistency
        await fetchRoles();
        
        setApiMessage({ text: 'Role updated successfully', type: 'success' });
        setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      } catch (error) {
        console.error('[RoleMaster] UpdateRole Error:', error);
        setApiMessage({ text: error.message || 'Failed to update role', type: 'error' });
        setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      }
    } else {
      try {
        const payload = buildCreateRolePayload(newRole);

        const response = await fetchApi('/api/RoleMaster/CreateRole', {
          method: 'POST',
          body: JSON.stringify(payload),
        });

        if (response?.didError) {
          setApiMessage({ text: response.errorMessage || 'Failed to create role', type: 'error' });
          setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
          return;
        }


        // Create Audit Log
        await fetchApi('/api/AuditLog/CreateAuditLog', {
          method: 'POST',
          body: JSON.stringify(buildAuditLogPayload(payload)),
          skipLoader: true
        }).catch(err => console.error('[RoleMaster] CreateAuditLog Error:', err));

        // Refresh from API to get consistent data
        await fetchRoles();

        setApiMessage({ text: 'Role created successfully', type: 'success' });
        setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      } catch (error) {
        console.error('[RoleMaster] CreateRole Error:', error);
        setApiMessage({ text: error.message || 'Failed to create role', type: 'error' });
        setTimeout(() => setApiMessage({ text: '', type: '' }), 3000);
      }
    }
    setShowModal(false);
    setModalMode('add');
    setEditIndex(null);
    setNewRole({ roleCode: '', roleDescription: '', status: true, sessionAccess: [] });
  };

  return (
    <Header>
    <div className="master-container">
      {apiMessage.text && (
        <div className={`api-message ${apiMessage.type}`}>
          {apiMessage.text}
        </div>
      )}
      <div className="master-header">
        <h4>Role & Authorization Master</h4>
      </div>

      <div className="master-toolbar">
        <button className="btn-add-new" onClick={() => { setModalMode('add'); setNewRole({ roleCode: '', roleDescription: '', status: true, sessionAccess: [] }); setShowModal(true); }}>+ Add New Role</button>
        <div className="toolbar-right">
          <div className="search-wrapper">
            <span className="search-icon">&#128269;</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search here..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <button className="btn-download" onClick={handleDownload} title="Download">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="master-table-wrapper">
        <table className="master-table">
          <thead>
            <tr>
              <th className="sortable-header" onClick={() => handleSort('status')}>Status {getSortIcon('status')}</th>
              <th>Role Code</th>
              <th className="sortable-header" onClick={() => handleSort('roleDescription')}>Role Description {getSortIcon('roleDescription')}</th>
              <th>Session Access</th>
              <th>Inserted By</th>
              <th>Inserted On</th>
              <th>Modified By</th>
              <th>Modified On</th>
              <th className="actions-header"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" className="no-data">Loading...</td>
              </tr>
            ) : paginatedRoles.length > 0 ? paginatedRoles.map((role, index) => (
              <tr key={index}>
                <td>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={role.status === 'Active'}
                      onChange={() => handleStatusToggle(role, roles.indexOf(role))}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="status-text" style={{ color: role.status === 'Active' ? 'green' : 'red', fontSize: '0.85em' }}>{role.status === 'Active' ? 'Active' : 'Inactive'}</span>
                </td>
                <td>{role.roleCode}</td>
                <td>{role.roleDescription}</td>
                <td>{role.sessionAccess}</td>
                <td>{role.insertedBy}</td>
                <td>{role.insertedOn}</td>
                <td>{role.modifiedBy}</td>
                <td>{role.modifiedOn}</td>
                <td className="actions-cell">
                  <button className="action-btn" onClick={() => handleView(role)}>
                    <img src={eyeIcon} alt="View" />
                  </button>
                  <button className="action-btn" onClick={() => handleEdit(role, roles.indexOf(role))}>
                    <img src={editIcon} alt="Edit" />
                  </button>
                  <button className="action-btn" onClick={() => handleViewHistory(role)} data-tooltip="View History">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 12a9 9 0 1 1 0 .01"/>
                      <path d="M3 4v5h5"/>
                      <polyline points="12 7 12 12 15 15"/>
                    </svg>
                  </button>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="9" className="no-data">No records found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="master-pagination">
        <span>{((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords}</span>
        <div className="pagination-controls">
          <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>&#x27E8;&#x27E8;</button>
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>&#x27E8;</button>
          <span>Page {currentPage} of {totalPages}</span>
          <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>&#x27E9;</button>
          <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>&#x27E9;&#x27E9;</button>
        </div>
      </div>

      {/* Add Role Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setModalMode('add'); setEditIndex(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5>{modalMode === 'edit' ? 'Edit Role' : modalMode === 'view' ? 'View Role' : 'Add Role'}</h5>
            <div className="modal-close" onClick={() => { setShowModal(false); setModalMode('add'); setEditIndex(null); }}>&times;</div>
            </div>

            <div className="modal-body">
              <div className="modal-form-row">
                <div className="modal-field">
                  <label className="modal-field-label">Role Code <span style={{color:'red'}}>*</span></label>
                  <input
                    type="text"
                    placeholder="Role Code"
                    value={newRole.roleCode}
                    onChange={(e) => setNewRole({ ...newRole, roleCode: e.target.value })}
                    disabled={modalMode === 'view' || modalMode === 'edit'}
                  />
                </div>
                <div className="modal-field">
                  <label className="modal-field-label">Role Description <span style={{color:'red'}}>*</span></label>
                  <input
                    type="text"
                    placeholder="Role Description"
                    value={newRole.roleDescription}
                    onChange={(e) => setNewRole({ ...newRole, roleDescription: e.target.value })}
                    disabled={modalMode === 'view'}
                  />
                </div>
                <div className="modal-field status-field">
                  <span className="status-label">Status</span>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={newRole.status}
                      onChange={(e) => setNewRole({ ...newRole, status: e.target.checked })}
                      disabled={modalMode === 'view'}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <span className="status-text">{newRole.status ? 'Active' : 'Inactive'}</span>
                </div>
              </div>

              <div className="session-access-section">
                <div className="session-access-header">
                  <span className="session-title">Session Access <span style={{color:'red'}}>*</span></span>
                  <div className="session-access-right">
                    <span className="access-label">Access</span>
                    <label className="select-all-label">
                      <input
                        type="checkbox"
                        checked={newRole.sessionAccess.length === sessionList.length}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        disabled={modalMode === 'view'}
                      />
                      Select All
                    </label>
                  </div>
                </div>

                  <div className="session-list">
                  {sessionList.map((session) => (
                    <div className={`session-item ${session.isParent ? 'session-parent' : 'session-child'}`} key={session.name}>
                      <span className="session-name">{session.name}</span>
                      <input
                        type="checkbox"
                        checked={newRole.sessionAccess.includes(session.name)}
                        onChange={() => handleSessionToggle(session.name)}
                        disabled={modalMode === 'view'}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-footer">
                {modalMode !== 'view' && (
                  <button className="btn-add-role" onClick={handleAddRole}>{modalMode === 'edit' ? 'Update' : 'Add'}</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* History Modal */}
      {showHistoryModal && (
        <div className="modal-overlay">
          <div className="modal-content history-modal">
            <div className="modal-header">
              <h5>View History</h5>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button className="btn-download" onClick={handleHistoryDownload} title="Download History">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                </button>
                <div className="modal-close" onClick={() => setShowHistoryModal(false)}>&times;</div>
              </div>
            </div>
            <div className="modal-body">
              <div className="master-table-wrapper">
                <table className="master-table history-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Role Code</th>
                      <th>Role Description</th>
                      <th>Session Access</th>
                      <th>Created By</th>
                      <th>Created On</th>
                      <th>Modified By</th>
                      <th>Modified On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyData.map((item, index) => (
                      <tr key={index}>
                        <td>{item.status}</td>
                        <td>{item.roleCode}</td>
                        <td>{item.roleDescription}</td>
                        <td>{item.sessionAccess}</td>
                        <td>{item.createdBy}</td>
                        <td>{item.createdOn}</td>
                        <td>{item.modifiedBy}</td>
                        <td>{item.modifiedOn}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </Header>
  );
};

export default RoleMaster;
